import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../services/utils";
import { editUserSchema } from "../../schemas/User.schema";
import {
  updateUser,
  updateUserRoles,
  updateUserAuthTags,
  updateUserSystems,
} from "../../services/userService";
import useFetchUserById from "../../hooks/useFetchUserById";
import useFetchUserRoles from "../../hooks/useFetchUserRoles";
import useFetchUserAuthTags from "../../hooks/useFetchUserAuthTags";
import useFetchUserSystems from "../../hooks/useFetchUserSystems";
import FlexBetween from "../../components/FlexBetween";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  AssignRole,
  AssignSystem,
  AssignTags,
} from "../../components/UserManagement";

const EditUser: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams<{ id: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for assignment components
  const [selectedRolesIds, setSelectedRolesIds] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedSystems, setSelectedSystems] = useState<number[]>([]);

  // State for form changes detection
  const [initialFormData, setInitialFormData] = useState<any>({
    userData: {},
    selectedRolesIds: [],
    selectedTags: [],
    selectedSystems: [],
  });
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Fetch user data
  const [userData] = useFetchUserById({
    setIsLoading,
    userId: id || null,
  });

  // Fetch user assignment data
  const [userAssignedRoles] = useFetchUserRoles({
    setIsLoading,
    userId: id || null,
  });

  const [userAssignedAuthTags] = useFetchUserAuthTags({
    setIsLoading,
    userId: id || null,
  });

  const [userAssignedSystems] = useFetchUserSystems({
    setIsLoading,
    userId: id || null,
  });

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      username: "",
      email: "",
      user_role_id: "",
      user_auth_type: "",
      third_party_user_id: "",
    },
    validationSchema: editUserSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        // Update existing user - send only the required fields for the new API
        const updatePayload = {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          user_auth_type: values.user_auth_type,
          third_party_user_id: values.third_party_user_id,
        };

        // Execute all API calls
        await Promise.all([
          updateUser(id!, updatePayload),
          updateUserRoles(id!, selectedRolesIds),
          updateUserAuthTags(id!, selectedTags),
          updateUserSystems(id!, selectedSystems),
        ]);

        showToast("User updated successfully", "success");
        navigate("/users");
      } catch (error: any) {
        showToast(
          error?.response?.data?.message || "Failed to update user",
          "error"
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Initialize form with user data
  useEffect(() => {
    if (userData) {
      const initialValues = {
        firstname: userData.firstname || "",
        lastname: userData.lastname || "",
        username: userData.username || "",
        email: userData.email || "",
        user_role_id: userData.user_role_id?.toString() || "",
        user_auth_type: userData.user_auth_type || "",
        third_party_user_id: userData.third_party_user_id || "",
      };
      formik.setValues(initialValues);
      setInitialFormData((prev: any) => ({
        ...prev,
        userData: initialValues,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Initialize assignment selections with fetched data
  useEffect(() => {
    if (userAssignedRoles?.length > 0) {
      // Extract role IDs from the response
      const roleIds =
        userAssignedRoles.map((item: any) => item.user_role_id) || [];
      setSelectedRolesIds(roleIds);
      setInitialFormData((prev: any) => ({
        ...prev,
        selectedRolesIds: roleIds,
      }));
    }
  }, [userAssignedRoles]);

  useEffect(() => {
    if (userAssignedAuthTags?.length > 0) {
      // Extract tag IDs from the response
      const authTagIds =
        userAssignedAuthTags.map((item: any) => item.auth_tag_id) || [];
      setSelectedTags(authTagIds);
      setInitialFormData((prev: any) => ({
        ...prev,
        selectedTags: authTagIds,
      }));
    }
  }, [userAssignedAuthTags]);

  useEffect(() => {
    if (userAssignedSystems?.length > 0) {
      // Extract system IDs from the response
      const systemIds =
        userAssignedSystems.map((item: any) => item.system_id) || [];
      setSelectedSystems(systemIds);
      setInitialFormData((prev: any) => ({
        ...prev,
        selectedSystems: systemIds,
      }));
    }
  }, [userAssignedSystems]);

  useEffect(() => {
    // Only check for changes if initialFormData is properly set
    if (Object.keys(formik.values).length > 0) {
      const hasFormChanges =
        JSON.stringify(formik.values) !==
          JSON.stringify(initialFormData.userData) ||
        JSON.stringify(selectedRolesIds) !==
          JSON.stringify(initialFormData.selectedRolesIds) ||
        JSON.stringify(selectedTags) !==
          JSON.stringify(initialFormData.selectedTags) ||
        JSON.stringify(selectedSystems) !==
          JSON.stringify(initialFormData.selectedSystems);
      setHasChanges(hasFormChanges);
    } else {
      // If initialFormData is not set yet, disable save button
      setHasChanges(false);
    }
  }, [
    formik.values,
    initialFormData,
    selectedRolesIds,
    selectedTags,
    selectedSystems,
  ]);

  // Handler functions for assignment components
  const handleRoleChange = (roleIds: number[]) => {
    setSelectedRolesIds(roleIds);
  };

  const handleTagChange = (tagIds: number[]) => {
    setSelectedTags(tagIds);
  };

  const handleSystemChange = (systemIds: number[]) => {
    setSelectedSystems(systemIds);
  };

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                First Name<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="firstname"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.firstname && formik.errors.firstname
                    ? "has-error"
                    : ""
                }`}
                error={!!(formik.touched.firstname && formik.errors.firstname)}
                helperText={formik.touched.firstname && formik.errors.firstname}
                value={formik.values.firstname}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Last Name<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="lastname"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.lastname && formik.errors.lastname
                    ? "has-error"
                    : ""
                }`}
                error={!!(formik.touched.lastname && formik.errors.lastname)}
                helperText={formik.touched.lastname && formik.errors.lastname}
                value={formik.values.lastname}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Username<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="username"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.username && formik.errors.username
                    ? "has-error"
                    : ""
                }`}
                error={!!(formik.touched.username && formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
                value={formik.values.username}
                disabled={true}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Email<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="email"
                name="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.email && formik.errors.email ? "has-error" : ""
                }`}
                error={!!(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                value={formik.values.email}
                disabled={true}
              />
            </Grid>

            {/* Assignment Components */}
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <AssignRole
                selectedRolesIds={selectedRolesIds}
                onRoleChange={handleRoleChange}
                disabled={isLoading}
                label="Assign Roles"
                placeholder="Select..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <AssignTags
                selectedTags={selectedTags}
                onTagChange={handleTagChange}
                disabled={isLoading}
                label="Assign Tags"
                placeholder="Select..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <AssignSystem
                selectedSystems={selectedSystems}
                onSystemChange={handleSystemChange}
                disabled={isLoading}
                label="Assign Systems"
                placeholder="Select systems..."
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            {/* <Button
              type="button"
              color="secondary"
              variant="contained"
              onClick={() => navigate("/users")}
              // sx={{ marginRight: 2 }}
              className="btn-orange btn-dark"
            >
              Cancel
            </Button> */}
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={!hasChanges}
            >
              <SaveOutlinedIcon /> &nbsp; Update
            </Button>
          </FlexBetween>
        </Box>
      </form>
    </Box>
  );
};

export default EditUser;
